import { FileContainer, process } from "am-tax-fe-core";
import { components, paths } from "../generated/generated-api";
import { uploadFile, uploadForm } from "../uploadUtils";
import { cachedApiUrl, client } from "../entities/entityApi";

export type BenchmarkReference = components["schemas"]["BenchmarkReferenceDto"];
export type BenchmarkInput = components["schemas"]["BenchmarkInputDto"];
export type BenchmarkOutput = components["schemas"]["BenchmarkOutputDto"];
export type CreateOrUpdateBenchmarkReference = NonNullable<paths["/api/benchmarkreferences"]["post"]["requestBody"]>["content"]["multipart/form-data"] & {
    Id?: string;
};
export type CreateOrUpdateBenchmarkInput = NonNullable<paths["/api/benchmarkinputs"]["post"]["requestBody"]>["content"]["multipart/form-data"] & {
    Id?: string;
};
export type CreateOrUpdateBenchmarkOutput = components["schemas"]["CreateOrUpdateBenchmarkOutputDto"];

// Benchmark Input API
export const getBenchmarkInputsAsync = async (): Promise<BenchmarkInput[]> => process(client().GET("/api/benchmarkinputs"));

export const getBenchmarkInputByIdAsync = async (id: string): Promise<BenchmarkInput> =>
    process(
        client().GET("/api/benchmarkinputs/{id}", {
            params: {
                path: {
                    id: id,
                },
            },
        }),
    );

export const getBenchmarkInputByClientIdAsync = async (clientId: string): Promise<BenchmarkInput[]> =>
    process(
        client().GET("/api/benchmarkinputs/client/{clientId}", {
            params: {
                path: {
                    clientId: clientId,
                },
            },
        }),
    );

export const generateBenchmarkAsync = async (id: string): Promise<unknown> =>
    process(
        client().POST("/api/benchmarkinputs/{benchmarkInputId}/generate", {
            params: {
                path: {
                    benchmarkInputId: id,
                },
            },
        }),
    );

export const createBenchmarkInputAsync = async (args: CreateOrUpdateBenchmarkInput, file: FileContainer) => {
    const benchmarkInputData: Record<string, string> = {
        ClientId: args.ClientId || "",
        Region: args.Region || "",
        ServiceType: args.ServiceType || "",
        MinimumAverageTotalRevenue: args.MinimumAverageTotalRevenue || "",
        MinimumThreeYearRevenue: args.MinimumThreeYearRevenue || "",
        MinimumTwoYearRevenue: args.MinimumTwoYearRevenue || "",
        MinimumOneYearRevenue: args.MinimumOneYearRevenue || "",
        MinimumInventory: args.MinimumInventory || "",
        MinimumInventoryThreeYear: args.MinimumInventoryThreeYear || "",
        MinimumInventoryTwoYear: args.MinimumInventoryTwoYear || "",
        MinimumInventoryOneYear: args.MinimumInventoryOneYear || "",
        MinimumPPE: args.MinimumPPE || "",
        MinimumPPEThreeYear: args.MinimumPPEThreeYear || "",
        MinimumPPETwoYear: args.MinimumPPETwoYear || "",
        MinimumPPEOneYear: args.MinimumPPEOneYear || "",
        InventorySalesRatio: args.InventorySalesRatio || "",
        ProfitLevelIndicatorMethod: args.ProfitLevelIndicatorMethod || "",
        IqrType: args.IqrType || "",
    };
    return await uploadFile(`${cachedApiUrl}/api/benchmarkinputs`, file, benchmarkInputData);
};

export const updateBenchmarkInputAsync = async (args: CreateOrUpdateBenchmarkInput, file: FileContainer | undefined) => {
    const benchmarkInputData: Record<string, string> = {
        ClientId: args.ClientId || "",
        Region: args.Region || "",
        ServiceType: args.ServiceType || "",
        MinimumAverageTotalRevenue: args.MinimumAverageTotalRevenue || "",
        MinimumThreeYearRevenue: args.MinimumThreeYearRevenue || "",
        MinimumTwoYearRevenue: args.MinimumTwoYearRevenue || "",
        MinimumOneYearRevenue: args.MinimumOneYearRevenue || "",
        MinimumInventory: args.MinimumInventory || "",
        MinimumInventoryThreeYear: args.MinimumInventoryThreeYear || "",
        MinimumInventoryTwoYear: args.MinimumInventoryTwoYear || "",
        MinimumInventoryOneYear: args.MinimumInventoryOneYear || "",
        MinimumPPE: args.MinimumPPE || "",
        MinimumPPEThreeYear: args.MinimumPPEThreeYear || "",
        MinimumPPETwoYear: args.MinimumPPETwoYear || "",
        MinimumPPEOneYear: args.MinimumPPEOneYear || "",
        InventorySalesRatio: args.InventorySalesRatio || "",
        ProfitLevelIndicatorMethod: args.ProfitLevelIndicatorMethod || "",
        IqrType: args.IqrType || "",
    };

    if (file !== undefined) {
        return await uploadFile(`${cachedApiUrl}/api/benchmarkinputs/${args.Id}`, file, benchmarkInputData, "PUT");
    } else {
        return await uploadForm(`${cachedApiUrl}/api/benchmarkinputs/${args.Id}`, benchmarkInputData);
    }
};

export const deleteBenchmarkInputAsync = async (id: string): Promise<void> =>
    process(
        client().DELETE("/api/benchmarkinputs/{id}", {
            params: {
                path: {
                    id: id,
                },
            },
        }),
    );

// Benchmark Output API
export const getBenchmarkOutputsAsync = async (): Promise<BenchmarkOutput[]> => process(client().GET("/api/benchmarkoutputs"));

export const getBenchmarkOutputByIdAsync = async (id: string): Promise<BenchmarkOutput> =>
    process(
        client().GET("/api/benchmarkoutputs/{id}", {
            params: {
                path: {
                    id: id,
                },
            },
        }),
    );

export const getBenchmarkOutputsByInputIdAsync = async (inputId: string): Promise<BenchmarkOutput[]> =>
    process(
        client().GET("/api/benchmarkoutputs/by-input/{inputId}", {
            params: {
                path: {
                    inputId: inputId,
                },
            },
        }),
    );

export const createBenchmarkOutputAsync = async (args: CreateOrUpdateBenchmarkOutput): Promise<BenchmarkOutput> =>
    process(client().POST("/api/benchmarkoutputs", { body: args }));

export const updateBenchmarkOutputAsync = async (id: string, args: CreateOrUpdateBenchmarkOutput): Promise<BenchmarkOutput> =>
    process(
        client().PUT("/api/benchmarkoutputs/{id}", {
            params: {
                path: {
                    id: id,
                },
            },
            body: args,
        }),
    );

export const deleteBenchmarkOutputAsync = async (id: string): Promise<void> =>
    process(
        client().DELETE("/api/benchmarkoutputs/{id}", {
            params: {
                path: {
                    id: id,
                },
            },
        }),
    );

// Benchmark Reference API
export const getBenchmarkReferencesAsync = async (): Promise<BenchmarkReference[]> => process(client().GET("/api/benchmarkreferences"));

export const getBenchmarkReferenceByIdAsync = async (id: string): Promise<BenchmarkReference> =>
    process(
        client().GET("/api/benchmarkreferences/{id}", {
            params: {
                path: {
                    id: id,
                },
            },
        }),
    );

export const createBenchmarkReferenceAsync = async (args: CreateOrUpdateBenchmarkReference, file: FileContainer | undefined) => {
    const benchmarkReferenceData: Record<string, string> = {
        Region: args.Region || "",
        ServiceType: args.ServiceType || "",
        BlobStorageLink: args.BlobStorageLink || "",
    };
    if (file !== undefined) {
        return await uploadFile(`${cachedApiUrl}/api/benchmarkreferences`, file, benchmarkReferenceData);
    } else {
        return await uploadForm(`${cachedApiUrl}/api/benchmarkreferences`, benchmarkReferenceData, "POST");
    }
};

export const updateBenchmarkReferenceAsync = async (args: CreateOrUpdateBenchmarkReference, file: FileContainer | undefined) => {
    const benchmarkReferenceData: Record<string, string> = {
        Region: args.Region || "",
        ServiceType: args.ServiceType || "",
    };

    if (file !== undefined) {
        await uploadFile(`${cachedApiUrl}/api/benchmarkreferences/${args.Id}`, file, benchmarkReferenceData, "PUT");
    } else {
        await uploadForm(`${cachedApiUrl}/api/benchmarkreferences/${args.Id}`, benchmarkReferenceData);
    }
};

export const deleteBenchmarkReferenceAsync = async (id: string): Promise<void> =>
    process(
        client().DELETE("/api/benchmarkreferences/{id}", {
            params: {
                path: {
                    id: id,
                },
            },
        }),
    );

export const downloadBenchmarkReferenceAsync = async (benchmarkReferenceId: string): Promise<Response> => {
    const result = await client().GET("/api/benchmarkreferences/{id}/file", {
        params: {
            path: {
                id: benchmarkReferenceId,
            },
        },
        parseAs: "stream",
    });

    if (!result.response || !result.response.ok) {
        throw new Error(`Failed to download benchmark reference: ${result.response?.statusText || "Unknown error"}`);
    }

    return result.response;
};

export const downloadBenchmarkOutputAsync = async (id: string): Promise<Response> => {
    const result = await client().GET("/api/benchmarkoutputs/{id}/file", {
        params: {
            path: {
                id: id,
            },
        },
        parseAs: "stream",
    });

    if (!result.response || !result.response.ok) {
        throw new Error(`Failed to download benchmark output: ${result.response?.statusText || "Unknown error"}`);
    }

    return result.response;
};

export const downloadBenchmarkTemplateAsync = async (): Promise<Response> => {
    const result = await client().GET("/api/benchmarkinputs/benchmark-template/download", {
        parseAs: "stream",
    });

    if (!result.response || !result.response.ok) {
        throw new Error(`Failed to download template: ${result.response?.statusText || "Unknown error"}`);
    }

    return result.response;
};
